/**
 * Resolves display names for a brief's pinned document ids.
 *
 * The brief stores bare ids (`unit_briefs.pinned_doc_ids`), and there is no
 * documents-by-id route, so names come from the two list endpoints the
 * document pickers already call: the bid's own documents, then the org
 * library (paged, archived included — a pin outlives an archive).
 *
 * Names are cached for the session at module level. The pickers hand over the
 * names they already know (`rememberDocumentNames`), so a fresh pin renders its
 * name immediately without a fetch. An id that still resolves nowhere after a
 * full pass is remembered as missing, so it is shown as unavailable rather than
 * refetched on every render.
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import type { Document } from '@/lib/schemas'

interface LibraryListResponse {
  documents: Document[]
  total?: number
}

/** The library handler's `LIBRARY_LIST_MAX_LIMIT`. */
const LIBRARY_PAGE_SIZE = 200
/** Upper bound on library pages walked for one resolve (2,000 documents). */
const MAX_LIBRARY_PAGES = 10

const nameCache = new Map<string, string>()
const missingIds = new Set<string>()
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/** Seed the cache with names a picker or suggestion already carries. */
export function rememberDocumentNames(names: Record<string, string>): void {
  let changed = false
  for (const [id, name] of Object.entries(names)) {
    if (!name || nameCache.get(id) === name) continue
    nameCache.set(id, name)
    missingIds.delete(id)
    changed = true
  }
  if (changed) notify()
}

/** Test seam — the cache is module state and would leak between tests. */
export function resetPinnedDocumentNameCache(): void {
  nameCache.clear()
  missingIds.clear()
}

function cacheDocuments(documents: Document[] | undefined): void {
  for (const document of documents ?? []) {
    if (document.id && document.name) nameCache.set(document.id, document.name)
  }
}

async function resolveNames(bidId: string, ids: string[]): Promise<void> {
  const stillMissing = () => ids.filter((id) => !nameCache.has(id))

  if (bidId) {
    try {
      cacheDocuments(await api.get<Document[]>(`/bids/${bidId}/documents`))
    } catch {
      // Fall through to the library — a failed bid read must not blank every name.
    }
  }

  for (let page = 0; page < MAX_LIBRARY_PAGES && stillMissing().length > 0; page++) {
    let response: LibraryListResponse
    try {
      response = await api.get<LibraryListResponse>('/library/documents', {
        limit: String(LIBRARY_PAGE_SIZE),
        offset: String(page * LIBRARY_PAGE_SIZE),
        include_archived: 'true',
      })
    } catch {
      break
    }
    cacheDocuments(response.documents)
    if ((response.documents ?? []).length < LIBRARY_PAGE_SIZE) break
  }

  for (const id of stillMissing()) missingIds.add(id)
  notify()
}

function splitIds(idsKey: string): string[] {
  return idsKey ? idsKey.split(',') : []
}

export interface PinnedDocumentNames {
  /** id → file name, for every id that resolved. */
  names: Map<string, string>
  /** True while any id is neither resolved nor known to be missing. */
  isResolving: boolean
}

export function usePinnedDocumentNames(bidId: string, ids: string[]): PinnedDocumentNames {
  const [revision, setRevision] = useState(0)
  const idsKey = ids.join(',')

  useEffect(() => {
    const listener = () => setRevision((r) => r + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    const unresolved = splitIds(idsKey).filter((id) => !nameCache.has(id) && !missingIds.has(id))
    // resolveNames never rejects; it notifies every mounted instance when done.
    if (unresolved.length > 0) void resolveNames(bidId, unresolved)
  }, [bidId, idsKey])

  // `revision` is the cache's change signal — the cache itself is not state.
  return useMemo(() => {
    const names = new Map<string, string>()
    let isResolving = false
    for (const id of splitIds(idsKey)) {
      const name = nameCache.get(id)
      if (name) names.set(id, name)
      else if (!missingIds.has(id)) isResolving = true
    }
    return { names, isResolving }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, revision])
}
